'use client'

import type { MouseEvent } from 'react'

import { useRouter } from 'next/navigation'

import { Chip } from '@/app/components/atoms/Chip'
import { Link } from '@/app/components/atoms/Link'
import { ALL_LOCATIONS } from '@/app/lib/constants'
import { setManualLocation } from '@/app/lib/geolocation'
import { invalidateResolvedTargetCache } from '@/app/lib/hooks/useGeoLocationSync'

import styles from './ListingFilters.module.scss'
import type { ActiveFilterChip } from './types'

interface AppliedFilterChipsProps {
  /** Chips de filtros aplicados ya resueltos (ver `buildActiveFilterChips`). */
  readonly chips: readonly ActiveFilterChip[]
  /** Destino de "Limpiar todo": URL de la página sin filtros. Si se omite, no
   * se muestra el enlace (los chips se siguen quitando de uno en uno). */
  readonly clearAllHref?: string
  /** Se invoca al pulsar "Limpiar todo", antes de navegar (gancho de analítica
   * para la página consumidora). Opcional. */
  readonly onClearAll?: () => void
  /** Igual que `ListingFiltersProps.inverse` (ver su JSDoc): esta fila vive en
   * la misma franja inline que los dropdowns/chips de Fecha, así que sigue el
   * mismo fondo de la página anfitriona. `true` (histórico) = banda oscura →
   * chip en acento neón; `false` (p. ej. el módulo Agenda de canal, fondo
   * claro) → chip en blanco y negro, igual que el resto de la franja. */
  readonly inverse: boolean
}

/**
 * Fila de chips de "filtros aplicados" (paridad con `ActiveFilterChips` del DS
 * de referencia): un chip removible por filtro activo. Cada chip refleja un
 * parámetro de la URL y su ✕ lo quita; "Limpiar todo" (si hay `clearAllHref`)
 * navega a la página sin filtros.
 *
 * Render defensivo: sin chips no pinta nada (tampoco el enlace "Limpiar todo").
 */
/** `id` del chip de provincia (= `label` del campo en las páginas consumidoras). */
const PROVINCE_CHIP_ID = 'Provincia'

export function AppliedFilterChips({ chips, clearAllHref, onClearAll, inverse }: AppliedFilterChipsProps) {
  const router = useRouter()

  if (chips.length === 0) return null

  const hasProvinceChip = chips.some((chip) => chip.id === PROVINCE_CHIP_ID)

  // La provincia vive también en la cookie `location`: si "Limpiar todo" solo
  // navegara a `…/todos`, `useGeoLocationSync` releería la cookie anterior y
  // redirigiría de vuelta a la provincia (el filtro "no se quitaba"). Se
  // resetea la cookie ANTES de navegar, igual que al quitar el chip.
  async function handleClearAll(event: MouseEvent<HTMLAnchorElement>) {
    onClearAll?.()
    if (!hasProvinceChip || !clearAllHref) return
    event.preventDefault()
    await setManualLocation(ALL_LOCATIONS)
    invalidateResolvedTargetCache()
    router.push(clearAllHref)
  }

  return (
    <div className={styles.appliedChips}>
      <ul className={styles.appliedChipsList} aria-label="Filtros activos">
        {chips.map((chip) => (
          <li key={chip.id} className={styles.appliedChipItem}>
            {/* `selected`: un filtro aplicado es una selección activa, igual
                que el chip de fecha activo — mismo color de acento neón sobre
                banda oscura (`inverse`), o negro/blanco sobre fondo claro. */}
            <Chip label={chip.label} inverse={inverse} selected removable onRemove={chip.onRemove} />
          </li>
        ))}
      </ul>
      {clearAllHref && (
        <Link href={clearAllHref} dark size="s" className={styles.clearAll} onClick={handleClearAll}>
          Limpiar todo
        </Link>
      )}
    </div>
  )
}
